import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import ChannelSelector from "./booking/ChannelSelector";
import BookingForm from "./booking/BookingForm";
import SuccessCard from "./booking/SuccessCard";
import useAvailability from "./booking/useAvailability";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  name: "",
  email: "",
  phone: "",
  company: "",
  service: "site-vitrine",
  message: "",
};

export default function Booking() {
  const [date, setDate] = useState(undefined);
  const [time, setTime] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const { isoDate, slots, loadingSlots } = useAvailability(date);

  // Reset selected time when the date changes (slots list changes underneath)
  const handleDateChange = (d) => {
    setDate(d);
    setTime("");
  };

  const updateField = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isoDate || !time) {
      toast.error("Sélectionnez une date et un créneau horaire.");
      return;
    }
    if (!form.name || !form.email) {
      toast.error("Indiquez votre prénom et votre email.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/bookings`, {
        ...form,
        date: isoDate,
        time,
      });
      setSuccess(res.data);
      toast.success("Rendez-vous confirmé.");
    } catch (err) {
      const msg =
        err.response?.data?.detail || "Une erreur est survenue. Réessayez.";
      toast.error(typeof msg === "string" ? msg : "Erreur de réservation.");
    } finally {
      setSubmitting(false);
    }
  };

  const resetBooking = () => {
    setSuccess(null);
    setDate(undefined);
    setTime("");
    setForm(emptyForm);
  };

  return (
    <section
      id="reservation"
      data-testid="booking-section"
      className="py-20 md:py-28"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 items-end mb-12 md:mb-16">
          <div className="col-span-12 md:col-span-7">
            <span className="eyebrow">— Réservation</span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight font-medium text-[#0B1027] mt-5 leading-[1.05]">
              Discutons de votre projet,{" "}
              <em className="not-italic italic font-light text-[#0B1027]">
                sans engagement.
              </em>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 md:col-start-9">
            <p className="text-[#0F172A]/75 leading-relaxed text-base md:text-lg">
              Choisissez un créneau qui vous arrange. Vous repartez avec une
              vision claire de votre projet, qu'il aboutisse ensemble ou non.
            </p>
          </div>
        </div>

        {success ? (
          <SuccessCard booking={success} onReset={resetBooking} />
        ) : (
          <>
            <ChannelSelector />
            <BookingForm
              date={date}
              setDate={handleDateChange}
              isoDate={isoDate}
              slots={slots}
              loadingSlots={loadingSlots}
              time={time}
              setTime={setTime}
              form={form}
              updateField={updateField}
              submitting={submitting}
              onSubmit={handleSubmit}
            />
          </>
        )}
      </div>
    </section>
  );
}
